
import React, { memo, useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth.tsx';
import { isInRecoveryMode } from '@/utils/deployment/recoveryUtils';
import { preloadCriticalImages } from '@/utils/performance/imageOptimizer';
import { lazyLoad, LazyLoadWrapper } from '@/utils/performance/lazyLoad';
import ErrorBoundary from '@/components/ErrorBoundary';
import { LoadingState } from '@/components/ui/loading-state';

// Define authorized email as a constant for admin-only sections
const AUTHORIZED_EMAIL = 'acremades@gmail.com';

// Lazy load non-critical components with standardized approach
const RecoveryModeNotification = lazyLoad(() => 
  import('@/components/system/RecoveryModeNotification')
);

const PerformanceOptimizer = lazyLoad(() => 
  import('@/components/system/PerformanceOptimizer')
);

// Eagerly load critical components
const DashboardContainer = lazyLoad(
  () => import('@/components/dashboard/DashboardContainer'),
  { preload: true }
);

const Dashboard = memo(() => {
  const { user } = useAuth();
  const [inRecoveryMode, setInRecoveryMode] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use memoization for derived state
  const isAuthorized = useMemo(() => {
    return user?.email === AUTHORIZED_EMAIL;
  }, [user?.email]);
  
  // Check recovery mode status only once on mount
  useEffect(() => {
    console.log('Dashboard mounting, user:', user?.email);
    
    // Preload dashboard critical images
    preloadCriticalImages([
      '/lovable-uploads/5ff0c1a6-9b40-485d-bb61-6427fd6e304e.png', // Logo
    ]);
    
    // Defer non-critical checks
    const timer = setTimeout(() => {
      try {
        setInRecoveryMode(isInRecoveryMode());
        setIsLoading(false);
      } catch (error) {
        console.error('Error checking recovery mode:', error);
        setLoadError('Error initializing dashboard');
        setIsLoading(false);
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, [user]);

  // Display standardized error state
  if (loadError) {
    return (
      <div className="p-6 bg-white border border-red-100 rounded-lg shadow-sm">
        <div className="flex items-start gap-3">
          <div className="text-red-500 mt-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-medium text-gray-900">Dashboard Error</h2>
            <p className="mt-1 text-gray-600">{loadError}</p>
            <div className="mt-4">
              <button 
                onClick={() => window.location.reload()} 
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                Reload Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState message="Loading dashboard..." centered size="large" />;
  }

  // Use fragment to avoid unnecessary div nesting
  return (
    <ErrorBoundary componentName="DashboardContent">
      {/* Debug element to track rendering */}
      <div id="dashboard-rendered" style={{ display: 'none' }} data-timestamp={Date.now()} />
      
      {/* Apply performance optimizations */}
      <LazyLoadWrapper>
        <PerformanceOptimizer />
      </LazyLoadWrapper>
      
      {inRecoveryMode && (
        <LazyLoadWrapper>
          <RecoveryModeNotification />
        </LazyLoadWrapper>
      )}
      
      <LazyLoadWrapper>
        <DashboardContainer isAuthorized={isAuthorized} />
      </LazyLoadWrapper>
    </ErrorBoundary>
  );
});

Dashboard.displayName = 'Dashboard';

export default Dashboard;
